import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import {
  Box,
  Button,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import { useAccountType, usePermission } from "@js/apps/common/hooks";
import { RouterLink } from "@js/components/link";
import { useShowSnackbarFromQueryParam } from "@js/hooks";
import { AppLayout } from "@js/layouts/app";
import { normalizePath } from "@js/utils/url";

type OwnProps = {
  children: React.ReactNode;
  pageTitle?: string;
  onSave?: () => void;
  onCancel?: () => void;
  isSaveDisabled?: boolean;
  onCancelDisabled?: boolean;
};

type MenuItem = {
  path: string;
  label: string;
};

export const SettingsLayout = ({
  children,
  pageTitle,
  onSave,
  onCancel,
  isSaveDisabled = false,
  onCancelDisabled = false,
}: OwnProps) => {
  const { pathname } = useLocation();
  const isMobile = useMediaQuery("sm");
  const { isFreelancer } = useAccountType();
  const canManageEmployer = usePermission(
    ENUMS.CustomPermission.CAN_MANAGE_EMPLOYER_ACCOUNT,
  );

  useShowSnackbarFromQueryParam();

  const showSaveAndCancelButtons =
    isFreelancer || pathname.includes("account_info");

  const items: MenuItem[] = useMemo(() => {
    const menuItems = [
      {
        path: "/settings/account_info/",
        label: "Account",
      },
      {
        path: "/settings/privacy_security/",
        label: "Privacy & Security",
      },
      ...(canManageEmployer || isFreelancer
        ? [
            {
              path: "/settings/payments/",
              label: "Payments",
            },
          ]
        : []),
      {
        path: "/settings/notifications/",
        label: "Notifications",
      },
    ];

    return menuItems;
  }, [canManageEmployer, isFreelancer]);

  return (
    <AppLayout pageTitle={pageTitle} spacing={isMobile ? 0 : 8} showFooter>
      <Grid
        container
        size={{
          xs: 12,
        }}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          mb: isMobile ? 3 : 0,
          gap: 1,
        }}
      >
        <Typography
          variant="display"
          component="h1"
          size={isMobile ? "small" : "medium"}
          fontWeight={400}
          className="dib underline"
        >
          Settings
        </Typography>
        {showSaveAndCancelButtons && (
          <Box>
            {onCancel && (
              <Button
                variant="secondary"
                onClick={onCancel}
                size="medium"
                disabled={onCancelDisabled}
              >
                Cancel
              </Button>
            )}
            {onSave && (
              <Button
                variant="primary"
                onClick={onSave}
                disabled={isSaveDisabled}
                size="medium"
                style={{ marginLeft: 8 }}
              >
                Save
              </Button>
            )}
          </Box>
        )}
      </Grid>
      <Grid
        size={{
          xs: 12,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Tabs
            value={normalizePath(pathname)}
            variant="scrollable"
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: 2,
                p: "0px 1px",
                // For feature Flag UnderlinedTabs Button remove
              },
            }}
          >
            {items.map(({ path, label }) => (
              <Tab
                key={label}
                value={normalizePath(path)}
                component={RouterLink}
                sx={{
                  py: "6px",
                }}
                to={path}
                label={
                  <Typography component="span" variant="label" size="medium">
                    {label}
                  </Typography>
                }
              />
            ))}
          </Tabs>
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 12,
        }}
      >
        {children}
      </Grid>
    </AppLayout>
  );
};
